import {
  formatContactEmailsInput,
  isLaborLineNonEmpty,
  isPartLineNonEmpty,
  parseContactEmails,
} from './buildClaimGenerationPayload';
import { parseParserStructuredAddress } from './claimIntakeParser';
import {
  isTireProductType,
  normalizeRepairOrderDateToMdy,
  type ClaimRepairEntry,
  type ContractData,
} from './claimIntakeUtils';
import { vehicleNotesToPayload, type VehicleNotesFormState, type VehicleNotesPayload } from './vehicleNotesUtils';
import type { AdditionalSourceItem } from './additionalInformationUpload';

export {
  calcLaborHoursFromAmountString,
  calcLaborRateFromAmountString,
  calcLineAmountString,
  calcUnitPriceFromAmountString,
  isAmountOnlyLaborLine,
  recalcLaborLinesForRateChange,
} from './labor-calculation.utils';

// ─── Address types ────────────────────────────────────────────────────────────

export type ClaimAddress = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
};

// ─── Payload section types ────────────────────────────────────────────────────

export type PoliciesPayload = {
  policy_id: string;
  product_id: string;
  policy_description: string;
  policy_coverage: string;
  policy_term: number | string;
  policy_in_service_date: string;
  policy_end_date: string;
  policy_inservice_mileage: number | string;
  policy_max_mileage: number | string;
  policy_coverage_code: string;
  policy_sale_date: string;
  policy_sale_odometer: number | string;
  policy_status: string | null;
};

export type ProductPayload = {
  product_id: string;
  product_model: string;
  product_name: string;
  product_serial_no: string;
  product_model_varient: string;
  product_manufactured_date: string;
  product_in_service_date: string;
  product_year: number | null;
  actor_id: string;
};

export type ClaimCustomerData = {
  name: string;
  phone_no: string;
  address: ClaimAddress;
};

export type ServicerPayload = {
  servicer_type: string;
  name: string;
  /** Null in the 3csmapper request so the API can populate it; string[] of addresses on save. */
  email: string[] | null;
  phone_no: string | null;
  contact_name: string | null;
  mobile_no: string | null;
  /** Yes/No — whether the contact wants SMS updates. */
  want_text: string | null;
  address: ClaimAddress | null;
  dealer_no: string | null;
};

export type PartRequestItem = {
  part_id: number;
  part_description: string;
  part_number: string;
  part_quantity: number;
  unit_cost: number;
  amount: number;
  [key: string]: unknown;
};

export type LaborRequestItem = {
  labor_id: number;
  labor_description: string;
  labor_opcode: string;
  labor_hours: number;
  amount: number;
  [key: string]: unknown;
};

export type ClaimRequest = {
  complaint_description: string;
  cause_description: string;
  corrective_action_description: string;
  tiretread?: number;
  parts: PartRequestItem[];
  labor: LaborRequestItem[];
};

export type UnifiedClaimPayload = {
  policies: PoliciesPayload;
  product: ProductPayload;
  /** Always populated from contract / form fields. */
  customer: ClaimCustomerData;
  servicer: ServicerPayload;
  failure_date: string;
  /** Repair order date — sent at root level alongside failure_date. */
  repair_order_date: string;
  /** Labor rate per hour — sent at root level. */
  labor_rate: number;
  requests: ClaimRequest[];
  failure_odometer: string;
  invoice_number: string;
  reimbursement_method: string;
  /** Payment notification email — only present when reimbursement_method is CONNEXPAY (credit card). */
  payment_email?: string;
  vehicle_arrival_method: string;
  vehicle_at_service_center: string;
  vehicle_notes: VehicleNotesPayload;
  /** Supporting files uploaded via Additional Information (presignedurl/generate response rows). */
  additional_sources?: AdditionalSourceItem[];
  /** Primary uploaded document file(s) — same shape as `additional_sources`. */
  sources?: AdditionalSourceItem[];
};

// ─── Contract section builders ────────────────────────────────────────────────

function contractPolicyStatus(contract: ContractData): string | null {
  const description = contract.status?.description?.trim();
  return description ? description : null;
}

function contractProductYear(contract: ContractData): number | null {
  const year = contract.vehicle?.vehicleYear;
  return year != null && Number.isFinite(year) ? year : null;
}

export function buildPoliciesFromContract(contract: ContractData): PoliciesPayload {
  return {
    policy_id: contract.contractNumber ?? '',
    product_id: contract.vehicle?.vin ?? '',
    policy_description: contract.coverage?.description ?? '',
    policy_coverage: contract.coverage?.productType?.description ?? '',
    policy_term: contract.coverage?.terms?.[0]?.termMonths ?? '',
    policy_in_service_date: contract.effectiveDate ?? '',
    policy_end_date: contract.expirationDate ?? '',
    policy_inservice_mileage: contract.effectiveOdometer ?? '',
    policy_max_mileage: contract.expirationOdometer ?? '',
    policy_coverage_code: contract.coverage?.productType?.code ?? '',
    policy_sale_date: (contract as Record<string, unknown>).saleDate as string ?? '',
    policy_sale_odometer: contract.saleOdometer ?? '',
    policy_status: contractPolicyStatus(contract),
  };
}

export function buildProductFromContract(contract: ContractData): ProductPayload {
  const inServiceDate = (contract as Record<string, unknown>).inServiceDate as string ?? '';
  const actorId =
    (contract as Record<string, unknown>).payee != null
      ? (((contract as Record<string, unknown>).payee as Record<string, unknown>)?.id as string ?? '')
      : '';
  return {
    product_id: contract.vehicle?.vin ?? '',
    product_model: contract.vehicle?.model ?? '',
    product_name: contract.vehicle?.make ?? '',
    product_serial_no: contract.vehicle?.vin ?? '',
    product_model_varient: contract.vehicle?.vehicleType ?? '',
    product_manufactured_date: inServiceDate,
    product_in_service_date: inServiceDate,
    product_year: contractProductYear(contract),
    actor_id: actorId,
  };
}

/** Builds a structured customer address pre-filled from the contract record. */
export function buildCustomerAddressFromContract(contract: ContractData): ClaimAddress {
  const a = contract.customer?.address;
  return {
    address1: a?.address1 ?? '',
    address2: a?.address2 ?? '',
    city: a?.city ?? '',
    state: a?.state ?? '',
    postal_code: a?.postalCode ?? '',
    country_code: a?.countryCode ?? a?.country ?? a?.country_code ?? '',
  };
}

export type CustomerFieldsFromContractSetters = Readonly<{
  onCustomerNameChange: (v: string) => void;
  onCustomerPhoneChange: (v: string) => void;
  onCustomerAddress1Change: (v: string) => void;
  onCustomerAddress2Change: (v: string) => void;
  onCustomerCityChange: (v: string) => void;
  onCustomerStateChange: (v: string) => void;
  onCustomerPostalCodeChange: (v: string) => void;
  onCustomerCountryCodeChange: (v: string) => void;
}>;

/** Applies customer name, phone, and address from a VIN contract lookup result. */
export function applyCustomerFieldsFromContract(
  contract: ContractData,
  setters: CustomerFieldsFromContractSetters
): void {
  const name = [contract.customer?.firstName, contract.customer?.lastName].filter(Boolean).join(' ').trim();
  setters.onCustomerNameChange(name);
  setters.onCustomerPhoneChange(contract.customer?.phone?.trim() ?? '');

  const addr = buildCustomerAddressFromContract(contract);
  setters.onCustomerAddress1Change(addr.address1);
  setters.onCustomerAddress2Change(addr.address2);
  setters.onCustomerCityChange(addr.city);
  setters.onCustomerStateChange(addr.state);
  setters.onCustomerPostalCodeChange(addr.postal_code);
  setters.onCustomerCountryCodeChange(addr.country_code);
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

export function parseNumericField(v: string): number {
  const n = Number.parseFloat(String(v).replace(/[$,]/g, '').trim());
  return Number.isFinite(n) ? n : 0;
}

/** Rounds to 2 decimal places (nearest cent). */
export function roundAmountToTwoDecimals(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100) / 100;
}

/** Display string for currency amounts — `$` prefix, 2 decimals, comma grouping. */
export function formatAmountDisplay(value: unknown): string {
  if (value == null) return '';
  const raw = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!raw) return '';
  const n = Number.parseFloat(raw.replace(/[$,]/g, ''));
  if (!Number.isFinite(n)) return raw;
  return `$${roundAmountToTwoDecimals(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Input display value (no `$`) — 2 decimals with comma grouping for large numbers. */
export function formatAmountInputDisplay(value: unknown): string {
  if (value == null) return '';
  const raw = typeof value === 'string' ? value.trim() : String(value).trim();
  if (!raw) return '';
  const n = Number.parseFloat(raw.replace(/[$,]/g, ''));
  if (!Number.isFinite(n)) return raw.replace(/[$,]/g, '');
  return roundAmountToTwoDecimals(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/** Strips `$` / commas from a currency input so state/payload stay plain numbers. */
export function sanitizeCurrencyAmountInput(raw: string): string {
  let out = '';
  let hasDot = false;
  for (const ch of raw) {
    if (ch >= '0' && ch <= '9') {
      out += ch;
    } else if (ch === '.' && !hasDot) {
      out += ch;
      hasDot = true;
    }
  }
  return out;
}

/** UI string for quantity × unit price (parts) or hours × rate (labor). Re-exported from labor-calculation.utils. */

function buildPartPayloadLine(
  p: ClaimRepairEntry['part_lines'][number],
  idx: number,
  existing?: Record<string, unknown>
): PartRequestItem {
  const qty = parseNumericField(p.part_quantity);
  const unitCost = parseNumericField(p.part_unit_price);
  const amount = qty * unitCost;
  return {
    ...(existing ?? {}),
    part_id: idx + 1,
    part_description: p.part_description.trim(),
    part_number: p.part_number.trim(),
    part_quantity: qty,
    unit_cost: unitCost,
    amount,
  };
}

function buildLaborPayloadLine(
  l: ClaimRepairEntry['labor_lines'][number],
  idx: number,
  laborRate: string,
  existing?: Record<string, unknown>
): LaborRequestItem {
  const hours = parseNumericField(l.labor_hours);
  const rate = parseNumericField(laborRate);
  const amountFromLine = parseNumericField(l.labor_amount);
  let amount: number;
  if (l.allowFixedValue) {
    amount = amountFromLine;
  } else if (l.labor_amount.trim() && (!l.labor_hours.trim() || hours <= 0)) {
    amount = amountFromLine;
  } else {
    amount = hours * rate;
  }
  return {
    ...(existing ?? {}),
    labor_id: idx + 1,
    labor_description: l.labor_description.trim(),
    labor_opcode: l.labor_op_code.trim(),
    labor_hours: hours,
    amount,
  };
}

export function buildRequests(
  repairs: ClaimRepairEntry[],
  laborRate: string,
  productType?: string
): ClaimRequest[] {
  const includeTireTread = isTireProductType(productType ?? '');
  return repairs.map((repair) => {
    const request: ClaimRequest = {
      complaint_description: repair.complaint.trim(),
      cause_description: repair.cause.trim(),
      corrective_action_description: repair.correction.trim(),
      parts: repair.part_lines
        .filter(isPartLineNonEmpty)
        .map((p, idx) => buildPartPayloadLine(p, idx)),
      labor: repair.labor_lines
        .filter(isLaborLineNonEmpty)
        .map((l, idx) => buildLaborPayloadLine(l, idx, laborRate)),
    };
    if (includeTireTread) {
      request.tiretread = parseNumericField(repair.tiretread);
    }
    return request;
  });
}

export { mergeClaimObjectWithFormEdits, syncClaimObjectWithRepairs } from './mergeClaimObjectFormEdits';

// ─── Extractors from API response ─────────────────────────────────────────────

function parseAddressFromRaw(raw: unknown): ClaimAddress {
  const parsed = parseParserStructuredAddress(raw);
  return {
    address1: parsed.address1,
    address2: parsed.address2,
    city: parsed.city,
    state: parsed.state,
    postal_code: parsed.postalCode,
    country_code: parsed.countryCode,
  };
}

function getFirstRow(claimBody: Record<string, unknown>): Record<string, unknown> | undefined {
  return Array.isArray(claimBody.data)
    ? (claimBody.data[0] as Record<string, unknown> | undefined)
    : undefined;
}

/**
 * Extracts the `customer` object returned by the 3csmapper API.
 * The API response puts the unified payload directly in `data[0]`.
 */
export function extractCustomerFromClaimBody(
  claimBody: Record<string, unknown>
): ClaimCustomerData | null {
  const firstRow = getFirstRow(claimBody);
  if (!firstRow) return null;

  const candidate = firstRow.customer;
  if (candidate == null || typeof candidate !== 'object' || Array.isArray(candidate)) return null;
  const c = candidate as Record<string, unknown>;

  const phone =
    typeof c.phone_no === 'string'
      ? c.phone_no
      : typeof c.phone_number === 'string'
        ? c.phone_number
        : '';

  return {
    name: typeof c.name === 'string' ? c.name : '',
    phone_no: phone,
    address: parseAddressFromRaw(c.address),
  };
}

/** Full servicer details returned by the 3csmapper API (populated in the response). */
export type ServicerApiData = {
  email: string;
  phone_no: string;
  address: ClaimAddress | null;
  dealer_no: string;
  /** Servicer classification from stream `servicer_classification` output (not `servicer.servicer_type`). */
  servicer_classification: string;
};

function readServicerClassificationFromServicer(servicer: Record<string, unknown>): string {
  const fromClassification = servicer.servicer_classification;
  if (typeof fromClassification === 'string' && fromClassification.trim()) return fromClassification.trim();
  return '';
}

/** Normalizes servicer classification to `FRANCHISE` or `INDEPENDENT` (uppercase). */
export function normalizeServicerClassification(value: string): string {
  const normalized = value.trim().toUpperCase();
  if (normalized === 'FRANCHISE') return 'FRANCHISE';
  if (normalized === 'INDEPENDENT') return 'INDEPENDENT';
  return value.trim().toUpperCase();
}

/** Merges stream-level `servicer_classification` into extracted servicer API data. */
export function mergeServicerApiData(
  data: ServicerApiData | null,
  servicerClassification?: string | null
): ServicerApiData | null {
  const fromOutputs = normalizeServicerClassification(servicerClassification ?? '');
  if (!data) {
    if (!fromOutputs) return null;
    return {
      email: '',
      phone_no: '',
      address: null,
      dealer_no: '',
      servicer_classification: fromOutputs,
    };
  }
  return {
    ...data,
    servicer_classification: fromOutputs || normalizeServicerClassification(data.servicer_classification),
  };
}

/**
 * Extracts servicer fields returned by servicer_info / 3csmapper.
 * The payload sends only servicer.name; the API may fill phone, address, dealer_no, and email.
 * `email` is not applied to the Contact Email form field (manual entry only).
 */
export function extractServicerFromClaimBody(
  claimBody: Record<string, unknown>
): ServicerApiData | null {
  const firstRow = getFirstRow(claimBody);
  if (!firstRow) return null;

  const servicer = firstRow.servicer;
  if (servicer == null || typeof servicer !== 'object' || Array.isArray(servicer)) return null;
  const s = servicer as Record<string, unknown>;

  return {
    email: formatContactEmailsInput(s.email),
    phone_no: typeof s.phone_no === 'string' ? s.phone_no : '',
    address: s.address != null && typeof s.address === 'object' && !Array.isArray(s.address)
      ? parseAddressFromRaw(s.address)
      : null,
    dealer_no: typeof s.dealer_no === 'string' ? s.dealer_no : '',
    servicer_classification: normalizeServicerClassification(readServicerClassificationFromServicer(s)),
  };
}

// ─── Main builder ─────────────────────────────────────────────────────────────

export type CustomerAddressFields = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
};

export type ServicerAddressFields = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  countryCode: string;
};

export type BuildUnifiedClaimPayloadParams = {
  contract: ContractData;
  /** Customer name (pre-filled from contract, user-editable). */
  customerName: string;
  /** Customer phone (pre-filled from contract, user-editable). */
  customerPhone: string;
  /** Customer address fields (pre-filled from contract, user-editable). */
  customerAddress: CustomerAddressFields;
  repairs: ClaimRepairEntry[];
  laborRate: string;
  servicerName: string;
  /** Servicer phone — populated from 3csmapper response, user-editable. */
  servicerPhone: string;
  /** Contact person at the service center. */
  servicerContactName: string;
  /** Mobile number for SMS contact. */
  servicerMobile: string;
  /** Yes/No — whether to text the mobile number. */
  wantText: string;
  contactEmail: string;
  /** Payment email when reimbursement is Credit Card (CONNEXPAY). */
  paymentEmail: string;
  /** Servicer address fields (from 3csmapper response, user-editable). */
  servicerAddress: ServicerAddressFields;
  /**
   * When true (for 3csmapper calls), only `servicer.name` and `servicer.address.postal_code` are sent.
   * Other servicer fields are null so the API can populate them after generation.
   */
  servicerDetailsNull?: boolean;
  /** Servicer `dealer_no` — only sent when servicer details are included (after 3csmapper). */
  servicerDealerNo?: string;
  mileageIn: string;
  invoiceNumber: string;
  repairOrderDate: string;
  arrivalMethod: string;
  atServiceCenter: string;
  reimbursement: string;
  vehicleNotes: VehicleNotesFormState;
  /** Supporting files from Additional Information — attached at root as `additional_sources`. */
  additionalSources?: AdditionalSourceItem[];
  /** Primary document upload(s) — attached at root as `sources`. */
  sources?: AdditionalSourceItem[];
  /** Contract product type code — controls TIRE-specific request fields (e.g. tiretread). */
  productType?: string;
};

function emptyServicerAddressWithPostal(postalCode: string): ClaimAddress {
  return {
    address1: '',
    address2: '',
    city: '',
    state: '',
    postal_code: postalCode.trim(),
    country_code: '',
  };
}

export function buildUnifiedClaimPayload(params: BuildUnifiedClaimPayloadParams): UnifiedClaimPayload {
  const customer: ClaimCustomerData = {
    name: params.customerName.trim(),
    phone_no: params.customerPhone.trim(),
    address: {
      address1: params.customerAddress.address1.trim(),
      address2: params.customerAddress.address2.trim(),
      city: params.customerAddress.city.trim(),
      state: params.customerAddress.state.trim(),
      postal_code: params.customerAddress.postalCode.trim(),
      country_code: params.customerAddress.countryCode.trim(),
    },
  };

  const servicer: ServicerPayload = params.servicerDetailsNull
    ? {
        servicer_type: '',
        name: params.servicerName.trim(),
        email: null,
        phone_no: null,
        contact_name: null,
        mobile_no: null,
        want_text: null,
        address: emptyServicerAddressWithPostal(params.servicerAddress.postalCode),
        dealer_no: null,
      }
    : {
        servicer_type: '',
        name: params.servicerName.trim(),
        email: parseContactEmails(params.contactEmail),
        phone_no: params.servicerPhone.trim(),
        contact_name: params.servicerContactName.trim(),
        mobile_no: params.servicerMobile.trim(),
        want_text: params.wantText.trim(),
        address: {
          address1: params.servicerAddress.address1.trim(),
          address2: params.servicerAddress.address2.trim(),
          city: params.servicerAddress.city.trim(),
          state: params.servicerAddress.state.trim(),
          postal_code: params.servicerAddress.postalCode.trim(),
          country_code: params.servicerAddress.countryCode.trim(),
        },
        dealer_no: (params.servicerDealerNo ?? '').trim(),
      };

  const repairOrderDate = normalizeRepairOrderDateToMdy(params.repairOrderDate);
  const isCreditCard = params.reimbursement === 'CONNEXPAY';

  const base: UnifiedClaimPayload = {
    policies: buildPoliciesFromContract(params.contract),
    product: buildProductFromContract(params.contract),
    customer,
    servicer,
    failure_date: repairOrderDate,
    repair_order_date: repairOrderDate,
    labor_rate: parseNumericField(params.laborRate),
    requests: buildRequests(params.repairs, params.laborRate, params.productType),
    failure_odometer: params.mileageIn.trim(),
    invoice_number: params.invoiceNumber.trim(),
    reimbursement_method: params.reimbursement,
    vehicle_arrival_method: params.arrivalMethod,
    vehicle_at_service_center: params.atServiceCenter,
    vehicle_notes: vehicleNotesToPayload(params.vehicleNotes),
  };

  if (isCreditCard) {
    base.payment_email = params.paymentEmail.trim();
  }
  if (params.additionalSources && params.additionalSources.length > 0) {
    base.additional_sources = params.additionalSources;
  }
  if (params.sources && params.sources.length > 0) {
    base.sources = params.sources;
  }

  return base;
}
